import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';

import { TFunction } from 'i18next';
import {
  Bell,
  CheckCheck,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BellOff,
  Loader2,
  Calendar,
  Users,
  FileText,
  Settings,
  MessageSquare,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { ScrollArea } from 'components/ui/scroll-area';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useBatchUserAvatars } from 'hooks/useBatchUserAvatars';
import { useUserId } from 'hooks/useCurrentUser';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { useNotifications, NotificationGroup } from 'hooks/useNotifications';
import { cn } from 'lib/utils';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { Notification, NotificationPriority } from 'types/notificationsTypes';

// Determine if notification is from a user or from the platform
const isUserNotification = (notification: Notification): boolean => {
  const origin = notification.origin?.toLowerCase() || '';
  return (
    origin.includes('meeting') ||
    origin.includes('edge') ||
    origin.includes('request') ||
    origin.includes('invite') ||
    origin.includes('message') ||
    !!notification.data?.requester_id ||
    !!notification.data?.from_user_uuid ||
    !!notification.data?.avatar_user_id
  );
};

// Get the user ID to show avatar for
const getAvatarUserId = (notification: Notification, currentUserId?: string): string | null => {
  // For meeting requests, show the other party
  if (notification.origin?.includes('meeting_request')) {
    if (notification.data?.requester_id === currentUserId) {
      return notification.data?.host_id || null;
    }
    return notification.data?.requester_id || null;
  }

  // For edge created, show the user who created
  if (notification.origin?.includes('edge.created')) {
    return notification.data?.from_user_uuid || null;
  }

  // Default: use avatar_user_id or requester_id
  return notification.data?.avatar_user_id || notification.data?.requester_id || null;
};

// Get platform icon based on notification origin
const getPlatformIcon = (notification: Notification) => {
  const origin = notification.origin?.toLowerCase() || '';
  const typeId = notification.type_id?.toLowerCase() || '';

  if (origin.includes('calendar') || origin.includes('agenda') || origin.includes('schedule')) {
    return Calendar;
  }
  if (origin.includes('team') || origin.includes('group') || origin.includes('collective')) {
    return Users;
  }
  if (origin.includes('form') || origin.includes('document') || origin.includes('report')) {
    return FileText;
  }
  if (origin.includes('config') || origin.includes('setting') || origin.includes('system')) {
    return Settings;
  }
  if (origin.includes('message') || origin.includes('chat') || origin.includes('comment')) {
    return MessageSquare;
  }

  // By type
  if (origin.includes('error') || typeId.includes('error')) return XCircle;
  if (origin.includes('warning') || typeId.includes('warning')) return AlertTriangle;
  if (origin.includes('success') || typeId.includes('success')) return CheckCircle;

  return Info;
};

// Get icon color based on notification type
const getNotificationColor = (notification: Notification) => {
  const origin = notification.origin?.toLowerCase() || '';
  const typeId = notification.type_id?.toLowerCase() || '';

  if (origin.includes('error') || typeId.includes('error'))
    return 'text-destructive bg-destructive/10';
  if (origin.includes('warning') || typeId.includes('warning')) return 'text-warning bg-warning/10';
  if (origin.includes('success') || typeId.includes('success')) return 'text-success bg-success/10';
  return 'text-info bg-info/10';
};

// Get priority color classes for badge
const getPriorityColor = (priority?: NotificationPriority): string => {
  switch (priority) {
    case 'critical':
      return 'bg-destructive text-destructive-foreground';
    case 'high':
      return 'bg-warning text-warning-foreground';
    case 'medium':
      return 'bg-info text-info-foreground';
    case 'low':
    default:
      return 'bg-muted text-muted-foreground';
  }
};

// Get priority label - only show for high/critical
const getPriorityLabel = (priority: NotificationPriority | undefined, t: TFunction): string | null => {
  switch (priority) {
    case 'critical':
      return t('notifications.priorityUrgent');
    case 'high':
      return t('notifications.priorityHigh');
    default:
      return null;
  }
};

// Format time for display
const formatTime = (dateStr: string, t: TFunction): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(hours / 24);

  if (minutes < 1) {
    return t('notifications.timeNow');
  } else if (minutes < 60) {
    return t('notifications.timeMinutes', { count: minutes });
  } else if (hours < 24) {
    return t('notifications.timeHours', { count: hours });
  } else {
    return t('notifications.timeDays', { count: days });
  }
};

// Format full date for modal
const formatFullDate = (dateStr: string, language: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(language, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Group label mapping
const getGroupLabels = (t: TFunction): Record<NotificationGroup, string> => ({
  today: t('notifications.groupToday'),
  yesterday: t('notifications.groupYesterday'),
  thisWeek: t('notifications.groupThisWeek'),
  older: t('notifications.groupOlder'),
});

// Avatar component that uses pre-fetched avatar URL from batch
interface UserAvatarProps {
  avatarUrl: string | null | undefined;
  fallback?: string;
  isUnread: boolean;
  isLoading?: boolean;
}

const UserAvatar = memo(({ avatarUrl, fallback, isUnread, isLoading }: UserAvatarProps) => {
  return (
    <div className="relative">
      <Avatar className="h-10 w-10 ring-2 ring-transparent">
        {isLoading ? (
          <AvatarFallback className="bg-primary/10">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </AvatarFallback>
        ) : (
          <>
            <AvatarImage src={avatarUrl ?? undefined} alt={fallback || 'User'} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
              {fallback?.slice(0, 2).toUpperCase() || 'US'}
            </AvatarFallback>
          </>
        )}
      </Avatar>
      {isUnread && (
        <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-primary border-2 border-background" />
        </span>
      )}
    </div>
  );
});

// Platform icon component
interface PlatformIconProps {
  notification: Notification;
  isUnread: boolean;
}

const PlatformIcon = memo(({ notification, isUnread }: PlatformIconProps) => {
  const Icon = getPlatformIcon(notification);
  const colorClasses = getNotificationColor(notification);
  const [textColor, bgColor] = colorClasses.split(' ');

  return (
    <div className="relative">
      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center', bgColor)}>
        <Icon className={cn('h-5 w-5', textColor)} />
      </div>
      {isUnread && (
        <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-primary border-2 border-background" />
        </span>
      )}
    </div>
  );
});

interface NotificationItemProps {
  notification: Notification;
  avatarUrl: string | null | undefined;
  avatarUserId: string | null;
  isAvatarLoading: boolean;
  t: TFunction;
  onMarkAsRead: (id: string) => void;
  onNavigate: (notification: Notification) => void;
  onOpenDetail: (notification: Notification) => void;
}

const NotificationItem = memo(
  ({
    notification,
    avatarUrl,
    avatarUserId,
    isAvatarLoading,
    t,
    onMarkAsRead,
    onNavigate,
    onOpenDetail,
  }: NotificationItemProps) => {
    const messageRef = useRef<HTMLParagraphElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const isFromUser = isUserNotification(notification);

    // Check if text is truncated
    useEffect(() => {
      const element = messageRef.current;
      if (element) {
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    }, [notification.message]);

    const handleClick = () => {
      if (!notification.is_read) {
        onMarkAsRead(notification.id);
      }

      // If text is truncated, show detail modal first
      if (isTruncated) {
        onOpenDetail(notification);
      } else {
        onNavigate(notification);
      }
    };

    return (
      <Card
        className={cn(
          'cursor-pointer transition-all duration-200',
          'hover:shadow-md hover:scale-[1.01]',
          !notification.is_read && 'border-l-2 border-l-primary bg-primary/5'
        )}
        onClick={handleClick}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            {/* Avatar or Icon */}
            <div className="flex-shrink-0">
              {isFromUser ? (
                <UserAvatar
                  avatarUrl={avatarUrl}
                  fallback={avatarUserId || notification.title}
                  isUnread={!notification.is_read}
                  isLoading={isAvatarLoading}
                />
              ) : (
                <PlatformIcon notification={notification} isUnread={!notification.is_read} />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p
                  className={cn(
                    'text-sm line-clamp-1 flex-1 min-w-0',
                    !notification.is_read ? 'font-semibold' : 'font-medium'
                  )}
                  title={notification.title}
                >
                  {notification.title}
                </p>
                {getPriorityLabel(notification.priority, t) && (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'text-[10px] px-1.5 py-0 flex-shrink-0',
                      getPriorityColor(notification.priority)
                    )}
                  >
                    {getPriorityLabel(notification.priority, t)}
                  </Badge>
                )}
              </div>
              <p
                ref={messageRef}
                className="text-xs text-muted-foreground line-clamp-2 mt-0.5"
                title={notification.message}
              >
                {notification.message}
              </p>
              <p className="text-xs text-primary font-semibold mt-1.5">
                {formatTime(notification.sent_at, t)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.notification.id === nextProps.notification.id &&
      prevProps.notification.is_read === nextProps.notification.is_read &&
      prevProps.avatarUrl === nextProps.avatarUrl &&
      prevProps.isAvatarLoading === nextProps.isAvatarLoading
    );
  }
);

interface NotificationGroupSectionProps {
  label: string;
  notifications: Notification[];
  avatarMap: Map<string, string | null>;
  isAvatarLoading: boolean;
  currentUserId?: string;
  t: TFunction;
  onMarkAsRead: (id: string) => void;
  onNavigate: (notification: Notification) => void;
  onOpenDetail: (notification: Notification) => void;
}

const NotificationGroupSection = memo(
  ({
    label,
    notifications,
    avatarMap,
    isAvatarLoading,
    currentUserId,
    t,
    onMarkAsRead,
    onNavigate,
    onOpenDetail,
  }: NotificationGroupSectionProps) => {
    if (notifications.length === 0) return null;

    return (
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky top-0 bg-background py-1 z-10">
          {label}
        </h4>
        <div className="space-y-2">
          {notifications.map(notification => {
            const isFromUser = isUserNotification(notification);
            const avatarUserId = isFromUser ? getAvatarUserId(notification, currentUserId) : null;
            const avatarUrl = avatarUserId ? avatarMap.get(avatarUserId) : null;

            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                avatarUrl={avatarUrl}
                avatarUserId={avatarUserId}
                isAvatarLoading={isAvatarLoading && isFromUser && !!avatarUserId}
                t={t}
                onMarkAsRead={onMarkAsRead}
                onNavigate={onNavigate}
                onOpenDetail={onOpenDetail}
              />
            );
          })}
        </div>
      </div>
    );
  }
);

// Detail Modal Component
interface NotificationDetailModalProps {
  notification: Notification | null;
  avatarMap: Map<string, string | null>;
  isAvatarLoading: boolean;
  currentUserId?: string;
  open: boolean;
  onClose: () => void;
  onNavigate: (notification: Notification) => void;
}

const NotificationDetailModal = ({
  notification,
  avatarMap,
  isAvatarLoading,
  currentUserId,
  open,
  onClose,
  onNavigate,
}: NotificationDetailModalProps) => {
  const { t, i18n } = useTranslation('common');

  if (!notification) return null;

  const isFromUser = isUserNotification(notification);
  const avatarUserId = isFromUser ? getAvatarUserId(notification, currentUserId) : null;
  const avatarUrl = avatarUserId ? avatarMap.get(avatarUserId) : null;

  const handleNavigate = () => {
    onClose();
    onNavigate(notification);
  };

  // Check if notification has a destination
  const hasDestination =
    notification.origin?.includes('meeting_request') ||
    notification.origin?.includes('edge.created') ||
    notification.data?.link ||
    notification.data?.url ||
    notification.data?.path;

  return (
    <GlobalModal
      open={open}
      title={notification.title}
      size="md"
      hideDefaultFooter
      onClose={onClose}
      className="z-[9999]"
    >
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar or Icon */}
        <div className="flex-shrink-0">
          {isFromUser ? (
            <UserAvatar
              avatarUrl={avatarUrl}
              fallback={avatarUserId || notification.title}
              isUnread={!notification.is_read}
              isLoading={isAvatarLoading && !!avatarUserId}
            />
          ) : (
            <PlatformIcon notification={notification} isUnread={false} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mt-1">
            {formatFullDate(notification.sent_at, i18n.language)}
          </p>
        </div>
      </div>

      <p className="text-sm text-foreground whitespace-pre-wrap mb-6">{notification.message}</p>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onClose}>
          {t('notifications.close')}
        </Button>
        {hasDestination && (
          <Button onClick={handleNavigate} className="gap-2">
            <ExternalLink className="h-4 w-4" />
            {t('notifications.viewDetails')}
          </Button>
        )}
      </div>
    </GlobalModal>
  );
};

const EmptyState = () => {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col items-center justify-center h-full py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <BellOff className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-center">{t('notifications.emptyTitle')}</h3>
      <p className="text-sm text-muted-foreground text-center mt-2 max-w-xs">
        {t('notifications.emptyDescription')}
      </p>
    </div>
  );
};

// Skeleton component that mimics the structure of a notification item
const NotificationSkeleton = () => (
  <div className="flex items-start gap-3 p-3 rounded-lg bg-card animate-pulse">
    {/* Avatar skeleton */}
    <div className="h-10 w-10 rounded-full bg-muted" />

    {/* Content skeleton */}
    <div className="flex-1 space-y-2">
      {/* Title line */}
      <div className="h-4 bg-muted rounded w-3/4" />
      {/* Message lines */}
      <div className="h-3 bg-muted rounded w-full" />
      <div className="h-3 bg-muted rounded w-2/3" />
      {/* Time */}
      <div className="h-3 bg-muted rounded w-20 mt-2" />
    </div>
  </div>
);

// Loading skeletons component that renders multiple skeletons with group headers
const LoadingSkeletons = () => (
  <div className="space-y-4 px-4">
    {/* Group header skeleton */}
    <div className="h-5 bg-muted rounded w-16 animate-pulse" />

    {/* 3 notification skeletons */}
    <div className="space-y-2">
      <NotificationSkeleton />
      <NotificationSkeleton />
      <NotificationSkeleton />
    </div>

    {/* Another group */}
    <div className="h-5 bg-muted rounded w-20 mt-4 animate-pulse" />
    <div className="space-y-2">
      <NotificationSkeleton />
      <NotificationSkeleton />
    </div>
  </div>
);

const NotificationsTab = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const menuRoutes = useMenuRoutes();
  const { selectMenuByDocumentId } = useMenuSelection();
  const userId = useUserId();
  const {
    groupedNotifications,
    markAsRead,
    markAllAsRead,
    markAllAsSeen,
    unreadCount,
    notifications,
    loading,
    error,
  } = useNotifications();

  // Track if seen has been marked to prevent multiple calls
  const hasMarkedSeenRef = useRef(false);

  // Mark all notifications as seen when the tab is opened (only once after initial load)
  useEffect(() => {
    if (notifications.length > 0 && !loading && !hasMarkedSeenRef.current) {
      hasMarkedSeenRef.current = true;
      markAllAsSeen();
    }
  }, [notifications.length, loading, markAllAsSeen]);

  // State for detail modal
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Extract all user IDs that need avatars (batch fetch solution for N+1 problem)
  const userIdsForAvatars = useMemo(() => {
    return notifications
      .filter(notification => isUserNotification(notification))
      .map(notification => getAvatarUserId(notification, userId ?? undefined))
      .filter((id): id is string => !!id);
  }, [notifications, userId]);

  // Batch fetch all avatars at once instead of N+1 individual requests
  const { avatarMap, isLoading: isAvatarLoading } = useBatchUserAvatars(userIdsForAvatars);

  const handleOpenDetail = useCallback((notification: Notification) => {
    setDetailNotification(notification);
    setIsDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setIsDetailOpen(false);
    setDetailNotification(null);
  }, []);

  // Handle navigation based on notification origin (same logic as NotificationCard)
  const handleNavigate = useCallback(
    (notification: Notification) => {
      if (notification.origin?.includes('meeting_request')) {
        // Meeting request notification - navigate to meetings page
        const route = menuRoutes.find(r => r.documentId === 'vpquicugxm4w5svpjji0b3ox');
        if (route) {
          const menuContext = {
            documentId: route.documentId,
            path: route.path,
            preserveLevel: true,
          };

          selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
          navigate(route.path, {
            state: {
              menuContext,
            },
          });
        }
      } else if (notification.origin?.includes('edge.created')) {
        // Edge creation notification - navigate to edge page with selected user
        const route = menuRoutes.find(r => r.documentId === 'c7jp5768k53sra3u6bv5ynxg');
        if (route) {
          const menuContext = {
            documentId: route.documentId,
            path: route.path,
            preserveLevel: true,
          };

          selectMenuByDocumentId(route.documentId, true, undefined, route.path, true);
          navigate(route.path, {
            state: {
              selectedUser: notification.data?.from_user_uuid,
              menuContext,
            },
          });
        }
      }
      // For other notification types, check if there's a link in data
      else if (notification.data?.link || notification.data?.url || notification.data?.path) {
        const link = notification.data.link || notification.data.url || notification.data.path;
        navigate(link);
      }
    },
    [menuRoutes, navigate, selectMenuByDocumentId]
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsRead();
  }, [markAllAsRead]);

  const handleOpenPreferences = useCallback(() => {
    navigate('/settings/profile?tab=notifications');
  }, [navigate]);

  const groupLabels = useMemo(() => getGroupLabels(t), [t]);
  const hasNotifications = notifications.length > 0;

  if (loading && notifications.length === 0) {
    return <LoadingSkeletons />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5 flex-shrink-0" />
            <span className="truncate">{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <Badge variant="default" className="text-xs flex-shrink-0">
                {unreadCount} {unreadCount === 1 ? t('notifications.newSingular') : t('notifications.newPlural')}
              </Badge>
            )}
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5 truncate">
            {t('notifications.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs gap-1.5 flex-shrink-0"
            >
              <CheckCheck className="h-4 w-4" />
              <span className="hidden sm:inline">{t('notifications.markAll')}</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleOpenPreferences}
            className="h-8 w-8 flex-shrink-0"
            title={t('notifications.configureNotifications')}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Error state */}
      {error && <div className="text-center py-4 text-sm text-destructive px-2">{error}</div>}

      {/* Notifications List */}
      {hasNotifications ? (
        <ScrollArea className="flex-1 -mx-4">
          <div className="space-y-4 pb-4 px-4">
            <NotificationGroupSection
              label={groupLabels.today}
              notifications={groupedNotifications.today}
              avatarMap={avatarMap}
              isAvatarLoading={isAvatarLoading}
              currentUserId={userId ?? undefined}
              t={t}
              onMarkAsRead={markAsRead}
              onNavigate={handleNavigate}
              onOpenDetail={handleOpenDetail}
            />
            <NotificationGroupSection
              label={groupLabels.yesterday}
              notifications={groupedNotifications.yesterday}
              avatarMap={avatarMap}
              isAvatarLoading={isAvatarLoading}
              currentUserId={userId ?? undefined}
              t={t}
              onMarkAsRead={markAsRead}
              onNavigate={handleNavigate}
              onOpenDetail={handleOpenDetail}
            />
            <NotificationGroupSection
              label={groupLabels.thisWeek}
              notifications={groupedNotifications.thisWeek}
              avatarMap={avatarMap}
              isAvatarLoading={isAvatarLoading}
              currentUserId={userId ?? undefined}
              t={t}
              onMarkAsRead={markAsRead}
              onNavigate={handleNavigate}
              onOpenDetail={handleOpenDetail}
            />
            <NotificationGroupSection
              label={groupLabels.older}
              notifications={groupedNotifications.older}
              avatarMap={avatarMap}
              isAvatarLoading={isAvatarLoading}
              currentUserId={userId ?? undefined}
              t={t}
              onMarkAsRead={markAsRead}
              onNavigate={handleNavigate}
              onOpenDetail={handleOpenDetail}
            />
          </div>
        </ScrollArea>
      ) : (
        <EmptyState />
      )}

      {/* Footer hint */}
      {hasNotifications && (
        <div className="pt-3 border-t mt-auto">
          <p className="text-xs text-center text-muted-foreground">
            {t('notifications.clickForDetails')}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      <NotificationDetailModal
        notification={detailNotification}
        avatarMap={avatarMap}
        isAvatarLoading={isAvatarLoading}
        currentUserId={userId ?? undefined}
        open={isDetailOpen}
        onClose={handleCloseDetail}
        onNavigate={handleNavigate}
      />
    </div>
  );
};

export default NotificationsTab;
